// @ts-nocheck

import React from 'react';
import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import PropTypes from 'prop-types';
import { yieldCallback } from '@zillow/yield-callback';

// Actions
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import UserSearchActions from 'app/shared/flux/actions/UserSearchActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

// Components
import AdvertisementWrapper from 'app/shared/modules/AdvertisementWrapper';
import InfoCard from 'app/shared/modules/MobileSRP/ListingCards/InfoCard';
import ListingWrapper from 'app/shared/modules/listing-cards/ListingWrapper';
import NoListingsFound from 'app/shared/modules/search-page/NoListingsFound';
import PagerContainer from 'app/shared/modules/pager/PagerContainer';
import SkeletonListingCardGroup from 'app/shared/modules/skeleton/SkeletonListingCardGroup';

// Misc / Utils
import { DEFAULT } from 'app/shared/models/Filter';
import { areaUtils_getSrpMaxPageLimit } from 'app/shared/utils/areaUtils';
import commuteUtils from 'app/shared/utils/commuteUtils';
import constants from 'app/shared/constants/ConstantsBundle';
import './style.scss';
import { emitTrackImpressionForPropertyCardEvent } from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';
import { noop } from 'lodash';

const { array, number, func, bool } = PropTypes;
const PAGINATION_UPPER_BOUND = 20;

class AreaListingsContainer extends React.Component {
  static propTypes = {
    listings: array,
    maxListingsPerPage: number,
    setMapView: func,
    isSkeleton: bool,
    listingsLoading: bool,
    currPage: number,
    maxPage: number,
    isSrpCtaExperiment: bool,
    dispatch: func,
    userPoint: PropTypes.object,
    filter: PropTypes.shape({
      commute: PropTypes.object,
      search: PropTypes.shape({
        slug: PropTypes.string,
      }),
      price: PropTypes.shape({
        max: PropTypes.number,
        min: PropTypes.number,
      }),
      bedrooms: PropTypes.array,
    }),
  };

  static defaultProps = {
    listings: [],
    isSkeleton: false,
    maxListingsPerPage: 0,
    setMapView: noop,
    listingsLoading: false,
    currPage: 1,
    maxPage: 1,
    isSrpCtaExperiment: false,
    dispatch: noop,
    userPoint: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      hideNativeAd: {
        slot1: false,
        slot2: false,
      },
      commuteTimesArray: [],
    };
  }

  componentDidMount() {
    const { filter, userPoint } = this.props;

    if (commuteUtils.isCommuteFilterSet(filter.commute) || userPoint) {
      this.fetchCommuteTimes();
    }
  }

  componentDidUpdate(prevProps) {
    const { currPage, filter, listings, userPoint } = this.props;
    const prevFilter = prevProps.filter;
    const commuteFilterSet = commuteUtils.isCommuteFilterSet(filter.commute);
    const listingsLoaded = isEmpty(prevProps.listings) && !isEmpty(listings);
    const pageChanged = prevProps.currPage !== currPage;

    const commuteFilterChanged =
      filter.commute.commuteTimeMode !== prevFilter.commute.commuteTimeMode ||
      filter.commute.commuteTime !== prevFilter.commute.commuteTime ||
      filter.commute.commuteMode !== prevFilter.commute.commuteMode ||
      filter.commute.commuteLats !== prevFilter.commute.commuteLats ||
      filter.commute.commuteLons !== prevFilter.commute.commuteLons;

    if (
      (listingsLoaded && (commuteFilterSet || userPoint)) ||
      (commuteFilterChanged && (commuteFilterSet || userPoint)) ||
      (pageChanged && (commuteFilterSet || userPoint))
    ) {
      this.fetchCommuteTimes();
    }
  }

  fetchCommuteTimes = () => {
    const { dispatch, listings, filter, userPoint } = this.props;

    if (!userPoint || isEmpty(listings)) {
      return;
    }

    const commuteTimesArray = [];

    const listingsGroup1 = listings.slice(0, 10);
    const listingsGroup2 = listings.slice(10, 20);
    const locationParams1 = commuteUtils.buildCommuteTimesRequest(filter.commute, listingsGroup1, userPoint);
    let locationParams2;

    if (!isEmpty(listingsGroup2)) {
      locationParams2 = commuteUtils.buildCommuteTimesRequest(filter.commute, listingsGroup2, userPoint);
    }

    dispatch(ListingEngineActions.getCommuteTime(locationParams1))
      .then((result = []) => {
        const formatCommuteMode = filter.commute.commuteMode ? filter.commute.commuteMode.toLowerCase() : 'driving';

        result.forEach((travelTime) => {
          commuteTimesArray.push(travelTime[formatCommuteMode]);
        });

        this.setState({ commuteTimesArray });

        if (locationParams2) {
          return dispatch(ListingEngineActions.getCommuteTime(locationParams2));
        }
        return [];
      })
      .then((result = []) => {
        if (isEmpty(result)) {
          return;
        }

        const formatCommuteMode = filter.commute.commuteMode ? filter.commute.commuteMode.toLowerCase() : 'driving';

        result.forEach((travelTime) => {
          commuteTimesArray.push(travelTime[formatCommuteMode]);
        });
        this.setState({ commuteTimesArray });
        return undefined;
      })
      .catch((error) => {
        this.props.dispatch(
          ErrorActions.errorHandler({
            error,
            errorLocation: 'component.commuteTimes.calcTransitTimes',
            errorClass: 'listingEngineActions',
          }),
        );
      });
  };

  hideAdElement = (slot, hideNativeAd) => {
    if (this.state.hideNativeAd[slot] !== hideNativeAd) {
      this.setState({
        hideNativeAd: {
          ...this.state.hideNativeAd,
          [slot]: hideNativeAd,
        },
      });
    }
  };

  yieldCurrentSearchClick = yieldCallback(() => {
    const { dispatch } = this.props;
    dispatch(UserSearchActions.listingOnCurrentSearchClicked());
  });

  handleListingClick = () => {
    this.yieldCurrentSearchClick();
  };

  trackImpression = (listing) => {
    emitTrackImpressionForPropertyCardEvent({ listing, triggerLocation: 'search_results_list' });
  };

  renderListingsArray = (shownListings) => {
    const { currPage, filter, isSkeleton, listings, listingsLoading, maxListingsPerPage, maxPage } = this.props;
    const hasActivePriceFilter = filter.price.max !== DEFAULT.price.max && filter.price.min !== DEFAULT.price.min;
    const hasActiveBedFilter = !isEqual(filter.bedrooms, DEFAULT.bedrooms);
    const bothInfoCardFiltersActive = hasActivePriceFilter && hasActiveBedFilter;
    const shouldRenderInfoCards =
      ((currPage === 1 && maxPage > 1) || isSkeleton || listingsLoading) && !bothInfoCardFiltersActive;
    const INFO_CARD_LOCATION = this.state.hideNativeAd?.slot1 ? 10 : 11;

    const renderedListings = map(shownListings, (listing, idx) => {
      const shouldLazyLoadListingCardPhotos = idx > 5;

      if (listing) {
        return (
          <ListingWrapper
            keepMapLocation
            key={listing.aliasEncoded}
            listing={listing}
            shouldLazyLoadListingCardPhotos={shouldLazyLoadListingCardPhotos}
            onClick={this.handleListingClick}
            commuteTime={this.state.commuteTimesArray[idx]}
            commuteMode={filter.commute?.commuteMode?.toUpperCase() || 'DRIVING'}
            showTransitTime={this.state.commuteTimesArray[idx] >= 0}
            trackImpression={this.trackImpression}
            triggerLocation="search_results"
            triggerObject="property_card"
          />
        );
      }
    });

    if (shouldRenderInfoCards) {
      renderedListings.splice(INFO_CARD_LOCATION, 0, <InfoCard key="InfoCards" />);
    }

    if (shownListings.length > 2 && currPage === 1) {
      return (
        <AdvertisementWrapper
          handleHideAdElement={this.hideAdElement}
          hideSlot1={this.state.hideNativeAd.slot1}
          hideSlot2={this.state.hideNativeAd.slot2}
        >
          {renderedListings}
        </AdvertisementWrapper>
      );
    }

    return renderedListings;
  };

  render() {
    const { listings, isSkeleton, listingsLoading, currPage, maxPage, setMapView, filter, maxListingsPerPage } =
      this.props;

    const nextPage = currPage + 1;
    const prevPage = currPage - 1;
    const startSlice = 0;
    const endSlice = maxListingsPerPage;
    const shownListings = listings.slice(startSlice, endSlice);

    const maxPageLimit = areaUtils_getSrpMaxPageLimit(maxPage, filter.search.slug);

    if ((shownListings.length < 1 && listingsLoading) || isSkeleton) {
      return (
        <div className="AreaListingsContainer-listings">
          <SkeletonListingCardGroup count={20} />
        </div>
      );
    }

    if (shownListings.length < 1) {
      return <NoListingsFound />;
    }

    const renderedListingsArray = this.renderListingsArray(shownListings);

    return (
      <>
        <ul className="AreaListingsContainer-listings">{renderedListingsArray}</ul>
        {/* Handles GA tracking when user scrolls to bottom of Listings Container */}
        <InView as="span" triggerOnce />
        <PagerContainer
          setMapView={setMapView}
          maxPage={maxPageLimit}
          prevPage={prevPage}
          currPage={currPage}
          nextPage={nextPage}
          itemsPerPage={maxListingsPerPage}
          paginationUpperBound={PAGINATION_UPPER_BOUND}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const activeUserPoint = state.user.userPoints.activeUserPoint;
  const maxBuildings = state.listings.totalBuildings;
  const userPoints = state.user.userPoints.destinations;
  const userPoint = userPoints[activeUserPoint] || null;
  const listings = state.listings.listingGroups.byCoords;

  return {
    currPage: Number(state.location.current.query.page || 1),
    isInitialSsrPage: state.app.isInitialSsrPage,
    filter: state.filter,
    isMobile: state.app.device.screenWidth === 'sm',
    listingsLoading: !state.app.fetchListingsByCoordsComplete,
    listings,
    maxPage: Math.max(Math.ceil(maxBuildings / constants.MAX_LISTINGS_PER_PAGE), 1),
    userPoint,
  };
};

export default connect(mapStateToProps)(AreaListingsContainer);
